import { z } from 'zod';
import type { Node, Edge } from 'reactflow';
import type { AIModel } from '@/types';
import type { ExecutionContext } from '@/lib/blueprintExecutor';
import { sendMessageToModel } from '@/services/ModelService';

const AllowedNodeTypes = [
  'system-prompt',
  'model-selector',
  'ai-response',
  'agentic-flow',
  'user-decision',
  'action',
  'api-call',
  'configuration',
  'knowledge-base',
  'data',
  'mcp',
  'intelligencexp',
  'conditional',
  'user-input',
] as const;

type AllowedNodeType = (typeof AllowedNodeTypes)[number];

const PositionSchema = z.object({ x: z.number(), y: z.number() });

const PatchNodeSchema = z.object({
  id: z.string().min(1),
  type: z.enum(AllowedNodeTypes),
  position: PositionSchema.optional(),
  data: z.record(z.any()).default({}),
});

const PatchEdgeSchema = z.object({
  id: z.string().optional(),
  source: z.string().min(1),
  target: z.string().min(1),
  sourceHandle: z.string().nullable().optional(),
});

export const BlueprintPatchSchema = z.object({
  summary: z.string().default(''),
  addNodes: z.array(PatchNodeSchema).default([]),
  addEdges: z.array(PatchEdgeSchema).default([]),
  // Which node(s) should be executed next.
  continueFrom: z.array(z.string()).default([]),
});

export type BlueprintPatch = z.infer<typeof BlueprintPatchSchema>;

function safeJsonParse(input: string): unknown {
  const trimmed = input.trim();
  // allow fenced codeblocks
  const m = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  const body = m ? m[1] : trimmed;
  return JSON.parse(body);
}

function makeId(prefix: string) {
  return `${prefix}-${Math.random().toString(16).slice(2)}-${Date.now().toString(16)}`;
}

export function applyBlueprintPatch(params: {
  nodes: Node[];
  edges: Edge[];
  patch: BlueprintPatch;
}): { nodes: Node[]; edges: Edge[]; addedNodeIds: string[] } {
  const { nodes, edges, patch } = params;

  const existingNodeIds = new Set(nodes.map((n) => n.id));
  const nextNodes: Node[] = [...nodes];

  const maxX = Math.max(...nodes.map((n) => (n.position?.x ?? 0)), 0);
  const baseY = Math.min(...nodes.map((n) => (n.position?.y ?? 0)), 180);

  const addedNodeIds: string[] = [];

  patch.addNodes.forEach((pn, idx) => {
    if (existingNodeIds.has(pn.id)) return;

    const pos = pn.position ?? { x: maxX + 240, y: baseY + idx * 120 };

    const data = {
      ...(pn.data || {}),
      type: pn.type,
      label: (pn.data as any)?.label ?? pn.id,
      description: (pn.data as any)?.description ?? '',
    };

    nextNodes.push({
      id: pn.id,
      type: pn.type,
      position: pos,
      data,
    } as any);

    existingNodeIds.add(pn.id);
    addedNodeIds.push(pn.id);
  });

  const nextEdges: Edge[] = [...edges];
  const existingEdgeKey = new Set(edges.map((e) => `${e.source}::${e.sourceHandle ?? ''}::${e.target}`));

  // allow edges that connect existing nodes to newly created ones
  for (const pe of patch.addEdges) {
    if (!existingNodeIds.has(pe.source) || !existingNodeIds.has(pe.target)) continue;

    const key = `${pe.source}::${pe.sourceHandle ?? ''}::${pe.target}`;
    if (existingEdgeKey.has(key)) continue;

    nextEdges.push({
      id: pe.id || makeId('e'),
      source: pe.source,
      target: pe.target,
      sourceHandle: pe.sourceHandle ?? undefined,
      animated: true,
      style: { strokeWidth: 2 },
    } as any);

    existingEdgeKey.add(key);
  }

  return { nodes: nextNodes, edges: nextEdges, addedNodeIds };
}

function summarizeBlueprint(nodes: Node[], edges: Edge[]) {
  const types = nodes.reduce<Record<string, number>>((acc, n) => {
    acc[n.type] = (acc[n.type] || 0) + 1;
    return acc;
  }, {});

  return {
    nodeCount: nodes.length,
    edgeCount: edges.length,
    nodeTypeCounts: types,
    nodes: nodes.slice(0, 120).map((n) => ({
      id: n.id,
      type: n.type,
      label: (n.data as any)?.label,
      description: (n.data as any)?.description,
    })),
  };
}

function pickModel(appModels: AIModel[], preferredId?: string) {
  if (preferredId) {
    const m = appModels.find((x) => x.id === preferredId);
    if (m) return m;
  }
  return appModels.find((m) => m.provider === 'openai' && m.apiName.includes('gpt')) || appModels[0];
}

export async function proposeBlueprintPatch(params: {
  goal: string;
  anchorNodeId: string;
  nodes: Node[];
  edges: Edge[];
  context: ExecutionContext;
  appModels: AIModel[];
}): Promise<BlueprintPatch | null> {
  const { goal, anchorNodeId, nodes, edges, context, appModels } = params;

  const model = pickModel(appModels, String(context.variables.selectedModelId || ''));
  if (!model) return null;

  const bp = summarizeBlueprint(nodes, edges);
  const lastOutput = String(context.variables.lastOutput ?? '');

  const system = `You are the Blueprint Evolver for a visual programming system.

TASK
- Propose a JSON patch that ADDS new nodes/edges to extend the Blueprint toward the user's goal.
- Do NOT delete or edit existing nodes.
- Do NOT add start/end nodes.

CONSTRAINTS
- Allowed node types: ${AllowedNodeTypes.join(', ')}
- Every new node MUST have: id, type, data (object). position is optional.
- Every new edge MUST have: source, target. id optional.
- You MUST connect the new work to the existing blueprint by adding at least one edge that originates from anchorNodeId="${anchorNodeId}".
- If you are unsure what to add, return an empty patch: {"summary":"","addNodes":[],"addEdges":[],"continueFrom":[]}

OUTPUT FORMAT
Return ONLY valid JSON that matches:
{
  "summary": string,
  "addNodes": Array<{ id: string, type: string, position?: {x:number,y:number}, data: object }>,
  "addEdges": Array<{ id?: string, source: string, target: string, sourceHandle?: string|null }>,
  "continueFrom": string[]
}
`; 

  const user = {
    goal,
    anchorNodeId,
    blueprint: bp,
    lastOutput,
  };

  const resp = await sendMessageToModel(
    [
      { role: 'system', content: system },
      { role: 'user', content: JSON.stringify(user) },
    ] as any,
    model,
    appModels,
    context.variables.aiConfig || {}
  );

  const text = String((resp as any)?.content ?? '').trim();
  if (!text) return null;

  let parsed: unknown;
  try {
    parsed = safeJsonParse(text);
  } catch {
    return null;
  }

  const out = BlueprintPatchSchema.safeParse(parsed);
  if (!out.success) return null;

  // Soft validation: ensure added node types are allowed (already via zod) and ids unique
  const existing = new Set(nodes.map((n) => n.id));
  const uniqueNew = new Set<string>();
  for (const n of out.data.addNodes) {
    if (existing.has(n.id) || uniqueNew.has(n.id)) {
      return null;
    }
    uniqueNew.add(n.id);
  }

  return out.data;
}
